import React, { useCallback } from 'react';
import { Button, Container } from 'react-bootstrap';
import { toast } from 'sonner';

// Import hooks
import { useSendInvoiceV4 } from '../../../../services/create-booking-v4/hooks/useSendInvoiceV4';
import { useSendEmailReservationOta } from '../../../../services/booking-email/hooks/useSendEmailReservationOta';

// Import tipe data
import { ShowBookingDataV4 } from '../../../../services/create-booking-v4/schema/ShowBookingResponseV4';

interface SendEmailContentPropsV4 {
    bookingData: ShowBookingDataV4;
}

const SendEmailContentV4: React.FC<SendEmailContentPropsV4> = ({ bookingData }) => {
    // Hook untuk mengirim invoice ke klien
    const { mutate: sendInvoiceToClient, isPending: isSendingInvoice } = useSendInvoiceV4();

    // Hook untuk mengirim voucher OTA ke hotel
    const { mutate: sendOtaVoucher, isPending: isSendingOtaVoucher } = useSendEmailReservationOta();

    // Mengambil data relevan dari prop
    const invoiceNumber = bookingData.booking[0]?.invoice_number;
    const voucherNumber = bookingData.booking[0]?.voucher_number;

    const handleSendInvoice = useCallback(() => {
        if (!invoiceNumber) {
            toast.error("Invoice number is not available.");
            return;
        }
        toast.loading('Sending invoice to client...', { id: 'send-invoice-client' });
        sendInvoiceToClient(invoiceNumber, {
            onSuccess: () => {
                toast.success('Invoice successfully sent to the client.', { id: 'send-invoice-client' });
            },
            onError: (error) => {
                toast.error(error.message || 'Failed to send invoice.', { id: 'send-invoice-client' });
            }
        });
    }, [sendInvoiceToClient, invoiceNumber]);

    const handleSendOtaVoucher = useCallback(() => {
        if (!voucherNumber) {
            toast.error("Voucher number is not available.");
            return;
        }
        toast.loading('Sending OTA voucher to hotel...', { id: 'send-ota-voucher' });
        sendOtaVoucher({ voucher_number: voucherNumber }, {
            onSuccess: () => {
                toast.success('OTA voucher successfully sent to the hotel.', { id: 'send-ota-voucher' });
            },
            onError: (error) => {
                toast.error(error.message || 'Failed to send OTA voucher.', { id: 'send-ota-voucher' });
            }
        });
    }, [sendOtaVoucher, voucherNumber]);

    return (
        <Container className="my-4">
            <div className="d-flex flex-column flex-md-row justify-content-between gap-3">

                {/* Tombol untuk mengirim Invoice ke Klien */}
                <div className="w-100">
                    <Button
                        variant="primary"
                        className="w-100"
                        onClick={handleSendInvoice}
                        disabled={isSendingInvoice || !invoiceNumber}
                    >
                        {isSendingInvoice ? 'Sending...' : 'Send Invoice to Client'}
                    </Button>
                </div>

                {/* Tombol untuk mengirim Voucher OTA, hanya muncul jika ada voucher */}
                {voucherNumber && (
                    <div className="w-100">
                        <Button
                            variant="success"
                            className="w-100"
                            onClick={handleSendOtaVoucher}
                            disabled={isSendingOtaVoucher}
                        >
                            {isSendingOtaVoucher ? 'Sending...' : 'Send OTA Voucher to Hotel'}
                        </Button>
                    </div>
                )}
            </div>
        </Container>
    );
};

export default SendEmailContentV4;